<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import Toast from 'primevue/toast';
import type { WsClientFrame } from '@neon-one/auctions-types';

import AppShell from '@/shell/components/AppShell.vue';
import ContextStrip from '@/shell/components/ContextStrip.vue';
import BottomNav from '@/shell/components/BottomNav.vue';
import SideNav from '@/shell/components/SideNav.vue';
import ConnectionLostBanner from '@/shell/components/ConnectionLostBanner.vue';
import EventPausedBanner from '@/shell/components/EventPausedBanner.vue';
import RegisterPromptBanner from '@/shell/components/RegisterPromptBanner.vue';
import OutbidSnackbar from '@/shell/components/OutbidSnackbar.vue';
import DemoStateToggle from '@/shell/components/DemoStateToggle.vue';
import BidSheet from '@/shell/components/BidSheet.vue';
import ErrorBoundary from '@/shell/error-boundary/ErrorBoundary.vue';
import { FeedbackFab } from '@neon-one/feedback-widget';
import { useWs } from '@/shell/stores/ws';
import { useSession } from '@/shell/stores/session';
import { useAuctionEvent } from '@/shell/stores/auctionEvent';
import { useOutbidToast } from '@/shell/stores/outbidToast';
import { useFeatureFlagsStore } from '@/shell/stores/featureFlags';
import { useReconnectRefresh } from '@/shell/lib/useReconnectRefresh';
import { resolveOutbidToast } from '@/shell/lib/resolveOutbidToast';
import { resolveVoidToast } from '@/shell/lib/resolveVoidToast';
import { env } from '@/env';
// FIXME: cross-feature import — extract to shell bus in Phase 3
import { useMyBidsStore } from '@/features/my-bids';
import { ActivationGateMount } from '@/features/activation';

const ws = useWs();
const session = useSession();
const auctionEvent = useAuctionEvent();
const outbid = useOutbidToast();
const featureFlags = useFeatureFlagsStore();
const myBids = useMyBidsStore();
const route = useRoute();
// Bottom nav (mobile) and side nav (tablet+) are gated independently.
// Routes like item-detail hide the bottom nav so the floating bid bar
// can take the mobile-bottom space, but at tablet+ the bid bar reflows
// in-flow and the side nav should stay visible. Use `hideSideNav` meta
// on a route that wants to suppress the side nav too (e.g., a focused
// flow where the nav would distract).
const showBottomNav = computed(() => !route.meta.hideBottomNav);
const showSideNav = computed(() => !route.meta.hideSideNav);

function getBidderIdentity() {
  return {
    type: 'bidder' as const,
    id: session.bidder?.uuid ?? '',
    email: session.bidder?.email,
  };
}

function getBidderAuctionContext() {
  const ev = auctionEvent.event;
  if (!ev) return null;
  return { id: ev.uuid, name: ev.name };
}

// Shell-level bidder subscription so the outbid snackbar fires regardless
// of which page the bidder is on. MyBidsPage subscribes to the same
// channel — the WS client allows multiple handlers per channel set.
let bidderChannels: string[] = [];
const bidderFrameHandler = (frame: WsClientFrame): void => {
  // Keep the bidder's my-bids store in sync so the Winning/Outbid pill
  // on catalog + wishlist cards updates immediately when a competing
  // bid lands.
  myBids.applyFrame(frame);
  const toast = resolveOutbidToast(frame, auctionEvent.event?.slug);
  if (toast) outbid.show(toast);
};

function subscribeBidder(): void {
  if (bidderChannels.length > 0) {
    ws.unsubscribe(bidderChannels, bidderFrameHandler);
    bidderChannels = [];
  }
  const uuid = session.bidder?.uuid;
  if (!uuid) return;
  bidderChannels = [`bidder:${uuid}`];
  ws.subscribe(bidderChannels, bidderFrameHandler);
  // Eagerly hydrate my-bids so the catalog + wishlist cards can render
  // Winning/Outbid pills without waiting for the bidder to visit
  // MyBidsPage. Cheap when the bidder has no bids yet. store.load() logs
  // its own failures (GT1-358) so this fire-and-forget call isn't silent.
  void myBids.load();
}

// GT1-442 — bid-void snackbar, resolved off the EVENT channel, not the
// bidder owner channel. A void action fans out on `bidder:{voidedBidderUuid}`,
// `item:{id}` and `event:{uuid}`; the bidder promoted to high bidder by that
// void is by definition NOT the voided bidder, so the owner channel alone
// could never deliver their "you're now the high bidder" notice. The event
// channel is the one channel both roles are always subscribed to, and it
// carries exactly one copy of the action — no cross-channel de-dupe needed.
// resolveVoidToast() returns null for a void the bidder has no stake in, and
// for an anonymous visitor (no bidder uuid).
const voidToastHandler = (frame: WsClientFrame): void => {
  const toast = resolveVoidToast(frame, auctionEvent.event?.slug, session.bidder?.uuid);
  if (!toast) return;
  outbid.show(toast);
  // GT1-442 — the 'now-winning' case is someone ELSE's void promoting this
  // bidder, so it can only ever reach them via the event channel (their own
  // bidder:{uuid} channel never carries another bidder's void). My Bids only
  // subscribes to this channel while MyBidsPage itself is mounted, so
  // without this, a bidder promoted while sitting on, say, the item detail
  // page keeps seeing a stale Outbid pill until they navigate to My Bids and
  // trigger a fresh load. noteOwnPlacement() also clears any stale
  // wsOutbid guard for this entity, same as after the bidder's own
  // placement.
  if (toast.variant === 'now-winning') {
    myBids.noteOwnPlacement(toast.itemId, toast.bundleId);
  }
};

// Shell-level event subscription so lifecycle frames (paused, resumed,
// extended, closed) reach the auctionEvent store on every page.
const eventFrameHandlers: Array<(frame: WsClientFrame) => void> = [
  auctionEvent.applyFrame,
  voidToastHandler,
];
let eventChannels: string[] = [];
function subscribeEvent(): void {
  if (eventChannels.length > 0) {
    for (const handler of eventFrameHandlers) ws.unsubscribe(eventChannels, handler);
    eventChannels = [];
  }
  const uuid = auctionEvent.event?.uuid;
  if (!uuid) return;
  eventChannels = [`event:${uuid}`];
  for (const handler of eventFrameHandlers) ws.subscribe(eventChannels, handler);
}

// Shell-level feature-flag subscription. `flags:all` is a global channel the
// backend scope guard allows for anonymous AND bidder connections alike, so
// this works whether or not a bidder session/JWT exists. A feature_flag.changed
// frame means some evaluable flag flipped — refetch the evaluated set (the
// `key` in the payload is informational). Subscribed once on mount and replayed
// automatically by ws.reconnect()'s handler snapshot; useReconnectRefresh below
// covers the refetch after a reconnect that may have missed a broadcast.
const flagsChannels = ['flags:all'];
const flagsFrameHandler = (frame: WsClientFrame): void => {
  if (frame.type === 'feature_flag.changed') {
    void featureFlags.reload();
  }
};

// A reconnect may have missed a feature_flag.changed broadcast while the socket
// was down — refetch the evaluated flag set once the socket is back.
useReconnectRefresh(() => featureFlags.reload());

// Connect for all users — anonymous connections may subscribe to public
// event/item channels for live bid prices. Bidder channel requires auth.
watch(
  () => session.isAuthenticated,
  async (authenticated) => {
    if (authenticated) {
      // Reconnect so the JWT handshake lets the server auto-bind the bidder
      // owner channel. Falls back to connect() on initial load (no client yet).
      await ws.reconnect().catch(() => undefined);
      subscribeEvent();
      subscribeBidder();
    } else {
      // Purge all bidder:* channels across every handler before reconnecting
      // so no page-level subscription (MyBidsPage, WinsPage, etc.) is replayed
      // on the anonymous connection.
      ws.unsubscribePrefix('bidder:');
      bidderChannels = [];
      await ws.reconnect().catch(() => undefined);
      subscribeEvent();
    }
  },
  { immediate: true },
);
watch(() => session.bidder?.uuid, () => {
  if (session.isAuthenticated) subscribeBidder();
});
watch(() => auctionEvent.event?.uuid, () => subscribeEvent());

// Browser tab title reflects the loaded auction so a bidder juggling
// several org tabs can tell them apart. Falls back to the static
// index.html title before the event resolves (or on a route with none).
watch(
  () => auctionEvent.event,
  (event) => {
    if (typeof document === 'undefined') return;
    document.title = event
      ? `${event.name} | ${event.organizationName} | Auctions by Neon One`
      : 'Auctions by Neon One';
  },
  { immediate: true },
);

// Mobile NAT timeouts and iOS Safari tab suspension can leave the socket
// in a zombie state — readyState says OPEN, but server-side DDB bindings
// point at a connectionId that no longer accepts frames. When the tab
// returns to the foreground after being hidden for more than 30 s, force
// a fresh socket so the bidder channel binding is replayed under a new id.
let hiddenSince: number | null = null;
const ZOMBIE_THRESHOLD_MS = 30_000;
function onVisibilityChange(): void {
  if (typeof document === 'undefined') return;
  if (document.visibilityState === 'hidden') {
    hiddenSince = Date.now();
    return;
  }
  if (document.visibilityState !== 'visible') return;
  const hiddenMs = hiddenSince ? Date.now() - hiddenSince : 0;
  hiddenSince = null;
  if (hiddenMs < ZOMBIE_THRESHOLD_MS) return;
  void ws.reconnect();
}

// Lane C1 — apply `?demo=paused` URL flag to the in-memory auction
// event so the paused-treatment edges (banner, disabled Bid CTAs,
// disabled BidSheet submit) can be demoed deterministically. Watches
// the event load + flag together so the patch lands once the event
// is hydrated.
watch(
  () => [auctionEvent.event?.slug, route.query.demo] as const,
  ([slug, demo]) => {
    if (!slug || !auctionEvent.event) return;
    const flag = typeof demo === 'string' ? demo : null;
    // Only apply demo override when explicitly requested — never force
    // isPaused to false, which would unpause a legitimately paused event.
    if (flag !== 'paused') return;
    if (!auctionEvent.event.isPaused) {
      auctionEvent.setMockEvent({ isPaused: true });
    }
  },
  { immediate: true },
);

onMounted(() => {
  // Subscribe to the global flag channel (allowed for anonymous + bidder) and
  // populate the store once on init. Empty object today (allow-list empty) —
  // proves the endpoint + WS wiring end-to-end for future bidder-facing flags.
  ws.subscribe(flagsChannels, flagsFrameHandler);
  void featureFlags.load();
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
  }
});

onUnmounted(() => {
  if (eventChannels.length > 0) {
    for (const handler of eventFrameHandlers) ws.unsubscribe(eventChannels, handler);
    eventChannels = [];
  }
  if (bidderChannels.length > 0) {
    ws.unsubscribe(bidderChannels, bidderFrameHandler);
    bidderChannels = [];
  }
  ws.unsubscribe(flagsChannels, flagsFrameHandler);
  featureFlags.cancelPendingRefresh();
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange);
  }
  ws.disconnect();
});
</script>

<template>
  <ErrorBoundary>
    <AppShell>
      <template #top>
        <!-- Header layout —
             Mobile:  ContextStrip on its own row; below it the search
                      accessory + register banner stack as their own rows.
             Tablet+: single horizontal row across the viewport.
                      Name+pill on the left (above the sidebar), search
                      accessory centered in the content area, register
                      banner pinned at the right.
             CatalogPage teleports its search bar into #shell-top-accessory. -->
        <div class="shell-header">
          <ContextStrip class="shell-header__name" />
          <div class="shell-header__accessory-zone">
            <div
              id="shell-top-accessory"
              class="shell-header__accessory"
            />
          </div>
          <RegisterPromptBanner class="shell-header__banner" />
        </div>
        <EventPausedBanner />
        <ConnectionLostBanner />
      </template>

      <template
        v-if="showSideNav"
        #side
      >
        <!-- Tablet+ left rail. CSS in AppShell hides this column at
             mobile (display: none), so SideNav doesn't paint there.
             BottomNav (below) is hidden by its own media query at
             tablet+, so only one nav is visible at any width. -->
        <SideNav />
      </template>

      <RouterView />

      <template
        v-if="showBottomNav"
        #bottom
      >
        <!-- Per-route accessory slot above the BottomNav. ItemDetailPage
             teleports its floating bid card here. The wrapper caps width
             to match the content well and provides the 20px side gutters
             so the floating card doesn't go edge to edge. -->
        <div class="shell-bottom-accessory">
          <div id="shell-bottom-accessory" />
        </div>
        <BottomNav class="shell__bottom-nav" />
      </template>
    </AppShell>
    <ActivationGateMount />
    <Toast />
    <!-- Fixed-position snackbar host: shows regardless of BottomNav. -->
    <div class="outbid-snackbar-host">
      <OutbidSnackbar />
    </div>
    <!-- Bid sheet lives at the shell so it can open over any route via
         ?bid=:itemId — bidder stays where they were (catalog, my-bids,
         wishlist, item-detail) when they tap Bid. -->
    <BidSheet />
    <DemoStateToggle v-if="env.demoMode" />
    <FeedbackFab
      v-if="env.feedbackWidget"
      spa-name="bidder-web"
      :api-base-url="env.apiBaseUrl"
      :get-user-identity="getBidderIdentity"
      :get-auction-context="getBidderAuctionContext"
      bottom-offset="calc(var(--app-space-48) + var(--app-space-32) + env(safe-area-inset-bottom))"
    />
  </ErrorBoundary>
</template>

<style scoped>
.shell-bottom-accessory {
  max-width: var(--app-well-max-width);
  margin: 0 auto;
  box-sizing: border-box;
}
.outbid-snackbar-host {
  position: fixed;
  left: 0;
  right: 0;
  /* Sits above BottomNav (~64px) + safe-area inset. On routes without
     the bottom nav (hideBottomNav meta) it still floats above the
     viewport edge with the inset. */
  bottom: calc(72px + env(safe-area-inset-bottom));
  padding: 0 var(--app-space-16);
  z-index: 40;
  pointer-events: none;
}
.outbid-snackbar-host > :deep(.outbid-snackbar) {
  pointer-events: auto;
  max-width: var(--app-well-max-width);
  margin: 0 auto;
}

/* Header layout:
   Mobile: column stack — ContextStrip, then search accessory, then
   register banner. Each child takes its natural width.
   Tablet+: one row across the viewport. The name (ContextStrip) sits at
   the far left above the sidebar. The accessory zone takes the middle
   and centers the search inside the same horizontal area as the content
   well below. The register banner is pinned at the right edge. */
.shell-header {
  display: flex;
  flex-direction: column;
}
.shell-header__accessory {
  min-width: 0;
}
@media (min-width: 641px) {
  /* Three-column grid so each zone aligns with its visual partner below:
       col 1 — minmax(sidebar, max-content): name+pill above the sidebar,
               allowed to grow past 240px when the title + pill don't fit
       col 2 — flex: search left-aligned, starts where the cards start
       col 3 — auto: register banner pinned to the right edge */
  .shell-header {
    display: grid;
    /* col 1: minimum sidenav width, can grow to max-content (title +
       pill) when there's room. When the viewport is tight, col 1
       shrinks toward sidenav-width and the ContextStrip's flex-wrap
       drops the pill below the title.
       col 2: search, never below 240px.
       col 3: auto = banner natural width. */
    grid-template-columns:
      minmax(var(--app-sidenav-width), max-content) minmax(240px, 1fr) auto;
    align-items: center;
    gap: var(--app-space-16);
    padding: var(--app-space-8) var(--app-space-20);
    box-sizing: border-box;
  }
  .shell-header__name {
    min-width: 0;
  }
  .shell-header__accessory-zone {
    display: flex;
    align-items: center;
    min-width: 0;
  }
  .shell-header__accessory {
    width: 100%;
    max-width: 480px;
  }
  .shell-header__banner {
    margin: 0;
    justify-self: end;
  }
}

/* BottomNav is mobile-only; SideNav takes over at tablet+. Both are
   declared at App.vue level so we control visibility from one place. */
@media (min-width: 641px) {
  .shell__bottom-nav,
  .shell-bottom-accessory {
    display: none;
  }
  .outbid-snackbar-host {
    /* No bottom nav at tablet+, so the snackbar floats above the safe
       area inset only. */
    bottom: calc(var(--app-space-16) + env(safe-area-inset-bottom));
  }
}
</style>
